import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Filter, MessageSquareText, Phone, Search, X } from "lucide-react";
import Seo, { breadcrumbs } from "../components/Seo";
import VehicleCard from "../components/VehicleCard";
import AboutScottBlurb from "../components/AboutScottBlurb";
import { getVehicles } from "../lib/api";
import { siteConfig } from "../config";
import { parseSmartSearch } from "../lib/searchParser";
import { NEW_CHIPS, USED_CHIPS } from "../lib/shortcutChips";

const SORTS = [
  ["featured", "Recommended"],
  ["newest", "Newest arrivals"],
  ["price-asc", "Price: Low to High"],
  ["price-desc", "Price: High to Low"],
  ["mileage-asc", "Mileage: Low to High"],
  ["mileage-desc", "Mileage: High to Low"],
  ["year-desc", "Year: New to Old"],
  ["year-asc", "Year: Old to New"],
  ["days-asc", "Days in Stock: Low to High"],
  ["days-desc", "Days in Stock: High to Low"],
];

const BODY_STYLES = [
  ["", "Any"],
  ["car", "Car"],
  ["suv", "SUV"],
  ["truck", "Truck"],
  ["van", "Van / Minivan"],
  ["commercial", "Commercial"],
  ["ev", "EV"],
];

// Phase C: every URL-aware filter key supported by the listing. Keys
// that aren't present in the URL collapse to "" so deep links stay clean.
const FILTER_KEYS = [
  "search", "make", "model", "trim", "body_style", "drivetrain", "fuel_type",
  "min_price", "max_price", "min_mileage", "max_mileage",
  "min_year", "max_year",
  "exterior_color", "interior_color", "transmission",
  "cab_size", "bed_length", "engine", "feature", "availability",
  "stock_number", "vin", "max_payment", "sort", "status",
];

function initFilters(search, baseFilters) {
  const sp = new URLSearchParams(search);
  const out = {};
  for (const k of FILTER_KEYS) out[k] = sp.get(k) || "";
  if (!out.sort) out.sort = "featured";
  if (!out.status && baseFilters.status) out.status = baseFilters.status;
  return out;
}

function buildSearchString(filters, baseFilters) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === "" || v === null || v === undefined || v === false) continue;
    if (k === "status" && baseFilters.status) continue; // page-locked
    if (k === "body_style" && baseFilters.body_style) continue;
    if (k === "sort" && v === "featured") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export default function InventoryList({
  pageTitle = "Inventory",
  h1,
  seoTitle,
  seoDescription,
  canonicalPath,
  baseFilters = {},
  showStatusTabs = false,
  intro,
}) {
  const loc = useLocation();
  const navigate = useNavigate();
  const baseKey = `${loc.pathname}|${JSON.stringify(baseFilters)}`;

  const [filters, setFilters] = useState(() => initFilters(loc.search, baseFilters));
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  // Determine inventory mode (drives chip set + filter visibility).
  const mode = baseFilters.status === "used" ? "used" : "new";
  const chipSet = mode === "used" ? USED_CHIPS : NEW_CHIPS;

  // Reset filters on route change (different list page or status lock).
  useEffect(() => {
    setFilters(initFilters(loc.search, baseFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseKey]);

  // Sync filter state with URL (history.replaceState — does not break back/fwd).
  useEffect(() => {
    const newSearch = buildSearchString(filters, baseFilters);
    if (newSearch !== loc.search) {
      navigate(`${loc.pathname}${newSearch}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Fetch on filter change.
  useEffect(() => {
    let on = true;
    const fetchItems = async () => {
      setLoading(true);
      const params = {};
      Object.entries({ ...filters, ...baseFilters }).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined && v !== false) params[k] = v;
      });
      // numeric coercions
      for (const k of ["min_price", "max_price", "min_mileage", "max_mileage", "min_year", "max_year", "max_payment"]) {
        if (params[k]) params[k] = Number(params[k]);
      }
      try {
        const d = await getVehicles(params);
        if (on) {
          setItems(d.items || []);
          setTotal(d.total ?? (d.items || []).length);
        }
      } catch {
        if (on) { setItems([]); setTotal(0); }
      } finally {
        if (on) setLoading(false);
      }
    };
    fetchItems();
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), JSON.stringify(baseFilters)]);

  const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v }));
  const setManyF = (patch) => setFilters((s) => ({ ...s, ...patch }));

  /** Smart-search submission: translate free text → structured filters. */
  const onSearchCommit = (raw) => {
    const parsed = parseSmartSearch(raw);
    if (!parsed.applied_tokens.length) {
      setF("search", raw);
      return;
    }
    const patch = { ...parsed.filters, search: parsed.residual_search || "" };
    // Don't allow smart-search to bust a status lock.
    if (baseFilters.status) delete patch.status;
    if (baseFilters.body_style) delete patch.body_style;
    setManyF(patch);
  };

  const clearAll = () => {
    const reset = {};
    for (const k of FILTER_KEYS) reset[k] = "";
    reset.sort = "featured";
    reset.status = baseFilters.status || "";
    setFilters(reset);
  };

  // Active filter chips (for the active-filter bar).
  const activeFilters = Object.entries(filters).filter(([k, v]) => {
    if (!v) return false;
    if (k === "sort" && v === "featured") return false;
    if (k === "status" && baseFilters.status) return false;
    if (k === "body_style" && baseFilters.body_style) return false;
    return true;
  });

  // Apply a shortcut chip.
  const applyChip = (chip) => {
    const reset = {};
    for (const k of FILTER_KEYS) reset[k] = "";
    reset.sort = filters.sort || "featured";
    reset.status = baseFilters.status || filters.status || "";
    Object.assign(reset, chip.filters);
    if (baseFilters.status) reset.status = baseFilters.status;
    if (baseFilters.body_style && !chip.filters.body_style) reset.body_style = baseFilters.body_style;
    setFilters(reset);
  };

  const jsonLd = useMemo(() => [
    breadcrumbs([
      { name: "Home", path: "/" },
      { name: pageTitle, path: canonicalPath || loc.pathname },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: seoTitle || pageTitle,
      url: `${siteConfig.links.siteDomain}${canonicalPath || loc.pathname}`,
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: items.length,
        itemListElement: items.slice(0, 20).map((v, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${siteConfig.links.siteDomain}/${v.status}-vehicles/${v.slug}`,
          name: `${v.year} ${v.make} ${v.model} ${v.trim || ""}`.trim(),
        })),
      },
    },
  ], [items, pageTitle, seoTitle, canonicalPath, loc.pathname]);

  const lockedBody = baseFilters.body_style !== undefined;
  const lockedStatus = baseFilters.status !== undefined;

  return (
    <>
      <Seo
        title={seoTitle || `${pageTitle} | Scott Sells Chevys`}
        description={seoDescription || "Search live vehicle inventory in Central Ohio with Scott Hall."}
        canonicalPath={canonicalPath || loc.pathname}
        jsonLd={jsonLd}
      />

      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-16">
          <h1 className="ssc-display text-3xl md:text-5xl font-extrabold">{h1 || pageTitle}</h1>
          {intro && <p className="mt-3 text-[var(--ssc-silver)] max-w-2xl">{intro}</p>}
        </div>
      </section>

      <section className="ssc-container py-6 md:py-10">
        <AboutScottBlurb
          heading={mode === "used" ? "Used Shopping Made Easier" : "Personal Help from Scott Hall"}
          text={mode === "used"
            ? "Shopping used can feel overwhelming. Scott helps make it easier by helping you compare mileage, price, features, vehicle history availability, condition, and options that fit your needs."
            : "Get personal help from Scott Hall, with over 15 years of experience helping Central Ohio drivers compare vehicles, trims, features, lease options, finance options, and availability."
          }
          testId="inventory-about-scott"
          className="mb-6"
        />
        {showStatusTabs && !lockedStatus && (
          <div className="flex gap-2 mb-4">
            {[["", "All"], ["new", "New"], ["used", "Used"]].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setF("status", k)}
                className={`ssc-chip ${filters.status === k ? "ssc-chip-active" : ""}`}
                data-testid={`status-tab-${k || "all"}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Shortcut chips (Phase C) */}
        <div
          className="flex flex-nowrap gap-2 overflow-x-auto pb-1 mb-3 -mx-1 px-1"
          data-testid="shortcut-chips-row"
        >
          {chipSet.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => applyChip(c)}
              className="ssc-chip whitespace-nowrap shrink-0 text-xs"
              data-testid={`chip-${c.key}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Smart search row */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="flex-1 flex items-center bg-white border border-[var(--ssc-silver)] rounded-2xl px-3">
            <Search size={18} className="text-[var(--ssc-ink-2)]" />
            <input
              value={filters.search}
              onChange={(e) => setF("search", e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSearchCommit(e.currentTarget.value); } }}
              onBlur={(e) => { if (e.currentTarget.value && filters.search === e.currentTarget.value) onSearchCommit(e.currentTarget.value); }}
              placeholder='Try "crew cab silverado under 30k", "used suv under 20k", "diesel truck"...'
              className="flex-1 py-3 px-2 outline-none"
              data-testid="inventory-search"
            />
            {filters.search && (
              <button onClick={() => setF("search", "")} aria-label="Clear search" data-testid="inventory-clear-search">
                <X size={16} />
              </button>
            )}
          </div>
          <select value={filters.sort} onChange={(e) => setF("sort", e.target.value)} className="ssc-select md:max-w-xs" data-testid="inventory-sort">
            {SORTS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="ssc-btn ssc-btn-outline md:hidden"
            data-testid="inventory-filter-toggle"
          >
            <Filter size={16} /> Filters{activeFilters.length ? ` · ${activeFilters.length}` : ""}
          </button>
        </div>

        {/* Active filter pills */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3 items-center" data-testid="active-filters-bar">
            <span className="text-[10px] uppercase tracking-wide font-bold text-[var(--ssc-ink-2)]">{activeFilters.length} active</span>
            {activeFilters.map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 rounded-full text-xs">
                <span className="font-bold">{k}</span>:&nbsp;{String(v)}
                <button type="button" onClick={() => setF(k, "")} aria-label={`Clear ${k}`} className="text-blue-700 hover:text-blue-900" data-testid={`clear-filter-${k}`}>
                  <X size={11} />
                </button>
              </span>
            ))}
            <button type="button" onClick={clearAll} className="text-xs font-bold text-red-700 hover:underline" data-testid="clear-all-filters">Clear All</button>
          </div>
        )}

        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Filters */}
          <aside className={`${filterOpen ? "block fixed inset-x-0 bottom-0 top-16 z-40 bg-[var(--ssc-paper)] overflow-y-auto p-4" : "hidden"} lg:block lg:static lg:p-0 lg:bg-transparent`}
                 data-testid="filter-panel">
            <div className="lg:ssc-card lg:sticky lg:top-24 space-y-4">
              <div className="flex items-center justify-between">
                <div className="font-extrabold text-[var(--ssc-navy)]">Filters</div>
                <button type="button" onClick={() => setFilterOpen(false)} className="lg:hidden text-[var(--ssc-ink-2)]" aria-label="Close filters" data-testid="filter-panel-close">
                  <X size={18} />
                </button>
              </div>

              {mode === "used" && (
                <div>
                  <label className="ssc-label">Make</label>
                  <input className="ssc-input" placeholder="Any make" value={filters.make} onChange={(e) => setF("make", e.target.value)} data-testid="filter-make" />
                </div>
              )}
              <div>
                <label className="ssc-label">Model</label>
                <input className="ssc-input" placeholder="Any model" value={filters.model} onChange={(e) => setF("model", e.target.value)} data-testid="filter-model" />
              </div>
              <div>
                <label className="ssc-label">Trim</label>
                <input className="ssc-input" placeholder="LT, RST, Z71, Trail Boss..." value={filters.trim} onChange={(e) => setF("trim", e.target.value)} data-testid="filter-trim" />
              </div>

              {!lockedBody && (
                <div>
                  <label className="ssc-label">Body Style</label>
                  <select className="ssc-select" value={filters.body_style} onChange={(e) => setF("body_style", e.target.value)} data-testid="filter-body">
                    {BODY_STYLES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="ssc-label">Drivetrain</label>
                <select className="ssc-select" value={filters.drivetrain} onChange={(e) => setF("drivetrain", e.target.value)} data-testid="filter-drivetrain">
                  <option value="">Any</option>
                  <option value="FWD">FWD</option>
                  <option value="AWD">AWD</option>
                  <option value="4WD">4WD</option>
                  <option value="RWD">RWD</option>
                  <option value="awd-4x4">AWD or 4x4</option>
                </select>
              </div>
              <div>
                <label className="ssc-label">Fuel Type</label>
                <select className="ssc-select" value={filters.fuel_type} onChange={(e) => setF("fuel_type", e.target.value)} data-testid="filter-fuel">
                  <option value="">Any</option>
                  <option value="gas">Gas</option>
                  <option value="electric">Electric</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="diesel">Diesel</option>
                  <option value="flex fuel">Flex Fuel</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="ssc-label">Min Price</label>
                  <input className="ssc-input" inputMode="numeric" value={filters.min_price} onChange={(e) => setF("min_price", e.target.value)} data-testid="filter-min-price" />
                </div>
                <div>
                  <label className="ssc-label">Max Price</label>
                  <input className="ssc-input" inputMode="numeric" value={filters.max_price} onChange={(e) => setF("max_price", e.target.value)} data-testid="filter-max-price" />
                </div>
              </div>

              {mode === "used" && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="ssc-label">Min Year</label>
                      <input className="ssc-input" inputMode="numeric" value={filters.min_year} onChange={(e) => setF("min_year", e.target.value)} data-testid="filter-min-year" />
                    </div>
                    <div>
                      <label className="ssc-label">Max Year</label>
                      <input className="ssc-input" inputMode="numeric" value={filters.max_year} onChange={(e) => setF("max_year", e.target.value)} data-testid="filter-max-year" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="ssc-label">Min Mileage</label>
                      <input className="ssc-input" inputMode="numeric" value={filters.min_mileage} onChange={(e) => setF("min_mileage", e.target.value)} data-testid="filter-min-mileage" />
                    </div>
                    <div>
                      <label className="ssc-label">Max Mileage</label>
                      <input className="ssc-input" inputMode="numeric" value={filters.max_mileage} onChange={(e) => setF("max_mileage", e.target.value)} data-testid="filter-max-mileage" />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="ssc-label">Max Payment ($/mo)</label>
                <input className="ssc-input" inputMode="numeric" value={filters.max_payment} onChange={(e) => setF("max_payment", e.target.value)} data-testid="filter-max-payment" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="ssc-label">Cab Size</label>
                  <select className="ssc-select" value={filters.cab_size} onChange={(e) => setF("cab_size", e.target.value)} data-testid="filter-cab">
                    <option value="">Any</option>
                    <option value="regular">Regular</option>
                    <option value="double">Double</option>
                    <option value="crew">Crew</option>
                  </select>
                </div>
                <div>
                  <label className="ssc-label">Bed Length</label>
                  <select className="ssc-select" value={filters.bed_length} onChange={(e) => setF("bed_length", e.target.value)} data-testid="filter-bed">
                    <option value="">Any</option>
                    <option value="short">Short</option>
                    <option value="standard">Standard</option>
                    <option value="long">Long</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="ssc-label">Exterior Color</label>
                <input className="ssc-input" placeholder="Any color" value={filters.exterior_color} onChange={(e) => setF("exterior_color", e.target.value)} data-testid="filter-exterior-color" />
              </div>
              <div>
                <label className="ssc-label">Interior Color</label>
                <input className="ssc-input" placeholder="Any color" value={filters.interior_color} onChange={(e) => setF("interior_color", e.target.value)} data-testid="filter-interior-color" />
              </div>

              {mode === "new" && (
                <div>
                  <label className="ssc-label">Availability</label>
                  <select className="ssc-select" value={filters.availability} onChange={(e) => setF("availability", e.target.value)} data-testid="filter-availability">
                    <option value="">Any</option>
                    <option value="available">Available Now</option>
                    <option value="in-transit">In Transit</option>
                  </select>
                </div>
              )}

              <details className="rounded-lg border border-[var(--ssc-line)] bg-[var(--ssc-paper)] px-3 py-2">
                <summary className="text-xs font-bold cursor-pointer">More filters</summary>
                <div className="mt-2 space-y-2">
                  <div>
                    <label className="ssc-label">Stock #</label>
                    <input className="ssc-input" value={filters.stock_number} onChange={(e) => setF("stock_number", e.target.value)} data-testid="filter-stock" />
                  </div>
                  <div>
                    <label className="ssc-label">VIN</label>
                    <input className="ssc-input" value={filters.vin} onChange={(e) => setF("vin", e.target.value)} data-testid="filter-vin" />
                  </div>
                  <div>
                    <label className="ssc-label">Feature</label>
                    <input className="ssc-input" placeholder="third_row, leather, sunroof..." value={filters.feature} onChange={(e) => setF("feature", e.target.value)} data-testid="filter-feature" />
                  </div>
                </div>
              </details>

              <button
                type="button"
                onClick={clearAll}
                className="ssc-btn ssc-btn-outline w-full"
                data-testid="filter-reset"
              >
                Clear All Filters
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="ssc-btn ssc-btn-primary w-full lg:hidden"
                data-testid="filter-apply"
              >
                Apply Filters
              </button>
            </div>
          </aside>

          {/* Results */}
          <div>
            <div className="text-[var(--ssc-ink-2)] mb-3 text-sm" data-testid="inventory-result-count">
              {loading ? "Loading..." : `${total} vehicle${total === 1 ? "" : "s"} found`}
            </div>
            {items.length === 0 && !loading ? (
              <div className="ssc-card text-center" data-testid="inventory-no-results">
                <div className="text-xl font-extrabold text-[var(--ssc-navy)]">
                  No exact matches right now.
                </div>
                <p className="mt-2 text-[var(--ssc-ink-2)]">
                  Text Scott and I can help find the right vehicle for you.
                </p>
                <div className="mt-4 flex justify-center gap-3 flex-wrap">
                  <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary" data-testid="inventory-empty-text-scott">
                    <MessageSquareText size={16} /> Text Scott
                  </a>
                  <a href={siteConfig.scott.phoneTel} className="ssc-btn ssc-btn-navy" data-testid="inventory-empty-call-scott">
                    <Phone size={16} /> Call Scott
                  </a>
                  <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-outline">Find Me a Vehicle</Link>
                  <button type="button" onClick={clearAll} className="ssc-btn ssc-btn-outline" data-testid="inventory-empty-clear">
                    Clear Filters
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {items.map((v) => <VehicleCard key={v.id || v.vin} vehicle={v} />)}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
